import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _findIndex from 'lodash/findIndex';
import _includes from 'lodash/includes';
import cx from 'classnames';

import { Tag } from '@zola/zola-ui/src/components/Tag';
import FeatureFlags from '../../../../util/featureFlags';

import './DropdownSelect.less';

class DropdownSelect extends Component {
  constructor(props) {
    super(props);
    this.selectedRefNode = React.createRef();
    this.directOptionsContainerRef = React.createRef();
    this.refNode = React.createRef();
    this.onWindowClick = this.onWindowClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.renderDropdownItem = this.renderDropdownItem.bind(this);
    this.renderDisabledDropdownItem = this.renderDisabledDropdownItem.bind(this);
    this.setScrollTopForScrollableContainer = this.setScrollTopForScrollableContainer.bind(this);
    this.setSelectedIndex = this.setSelectedIndex.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.state = {
      open: false,
      selectedIndex: null,
      selected: null,
    };
  }

  componentDidMount() {
    const { selected, alwaysOpen } = this.props;
    if (selected) {
      this.setSelectedIndex();
    }

    // Ignore all open/close logic when alwaysOpen is true
    if (alwaysOpen) {
      this.setState({ open: true });
      return;
    }

    window.addEventListener('click', this.onWindowClick);
    window.addEventListener('touchstart', this.onWindowClick);
  }

  componentDidUpdate(prevProps) {
    const { selected, options } = this.props;
    if (
      (selected && selected !== prevProps.selected) ||
      (options && options !== prevProps.options)
    ) {
      this.setSelectedIndex();
    }
    const {
      directOptionsContainerRef: { current: containerNode },
      selectedRefNode: { current: selectedNode },
    } = this;
    if (containerNode && selectedNode) {
      this.setScrollTopForScrollableContainer(containerNode, selectedNode);
    }
  }

  componentWillUnmount() {
    if (this.props.alwaysOpen) return;
    window.removeEventListener('click', this.onWindowClick);
    window.removeEventListener('touchstart', this.onWindowClick);
  }

  onWindowClick(e) {
    const { current: dropdownElement } = this.refNode;
    const { open } = this.state;

    e.stopPropagation();
    if (e.target !== dropdownElement && !dropdownElement.contains(e.target) && open) {
      this.hide();
    }
  }

  /**
   * Scrolls the container node to match the selected item
   * @param {HTMLDivElement} containerNode - Node containing the selectable items
   * @param {HTMLDivElement} selectedNode - The node to be scrolled to
   */
  setScrollTopForScrollableContainer(containerNode, { offsetTop }) {
    containerNode.scrollTop = offsetTop; // eslint-disable-line no-param-reassign
  }

  setSelectedIndex() {
    const { selected, options } = this.props;
    const isOptionsDropDown = options[0]?.elementName === 'options';
    const selectedValue = isOptionsDropDown ? { id: selected } : { value: selected };
    const getSelectedIndex = _findIndex(options, selectedValue);
    const selectedIndex = getSelectedIndex !== -1 ? getSelectedIndex : 0;
    this.setState({ selectedIndex, selected });
  }

  handleSelect(e, index, value) {
    const { alwaysOpen, onSelect, hasConfirmSelect, selectedType, options } = this.props;
    const isCustomizationSidePanelDropDown = options[0]?.elementName;
    if (alwaysOpen && !hasConfirmSelect) {
      this.setState({
        selectedIndex: index,
        selected: value,
      });
    } else {
      e.preventDefault();
      this.setState({
        open: false,
      });
      if (!hasConfirmSelect) {
        this.setState({
          selectedIndex: index,
          selected: value,
        });
      }
    }

    if (FeatureFlags.get('reviewQuantityUpdate') && selectedType) {
      onSelect(value, selectedType);
    } else if (isCustomizationSidePanelDropDown) {
      onSelect(e, options[index]);
    } else {
      onSelect(value);
    }
  }

  handleToggle(e) {
    e.preventDefault();
    const { alwaysOpen, isDisabled = false } = this.props;
    if (alwaysOpen || isDisabled) return;
    this.setState(prevState => ({
      open: !prevState.open,
    }));
  }

  hide() {
    if (this.props.alwaysOpen) return;
    this.setState({
      open: false,
    });
  }

  show() {
    if (this.props.alwaysOpen) return;
    this.setState({
      open: true,
    });
  }

  renderDisabledDropdownItem({ value, label }) {
    return (
      <div key={`${value}_${label}`} className="option option-disabled">
        {label}
      </div>
    );
  }

  renderDropdownItem(option, index, selectedIndex) {
    const { value, label, tagText } = option;
    const isSelected = index === selectedIndex;
    return (
      <div
        key={`${value}-${option?.id}`}
        ref={isSelected ? this.selectedRefNode : null}
        style={option.style}
        className={cx('option', {
          'option-selected': isSelected,
        })}
        onClick={e => this.handleSelect(e, index, value)}
      >
        {label}
        {tagText && tagText.length > 0 && (
          <>
            {' '}
            <Tag size="lg" className="option__tag option__tag__plum">
              {tagText}
            </Tag>
          </>
        )}
      </div>
    );
  }

  render() {
    const {
      options,
      disabledOptions,
      matchButtonV2,
      hideLabel,
      showOptionsOnTop = false,
      isDisabled,
      isRounded = false,
    } = this.props;
    const { open, selectedIndex } = this.state;

    return (
      <div
        className={cx('dropdown-select', {
          'dropdown-select--is-disabled': isDisabled,
          'dropdown-select--rounded': isRounded,
        })}
        ref={this.refNode}
      >
        {!hideLabel && (
          <div onClick={this.handleToggle}>
            {options[0].elementName === 'line-height' && (
              <i className="zola-ui-icon zolaicon-line-height" />
            )}
            <div
              className={cx('form-control', {
                expanded: open,
                'form-control--options-top': showOptionsOnTop,
                'form-control--options-bottom': !showOptionsOnTop,
                'form-control-match-btn-v2': matchButtonV2,
              })}
            >
              {selectedIndex !== null && options[selectedIndex] ? (
                <>
                  {options[selectedIndex].label}
                  {options[selectedIndex].tagText && options[selectedIndex].tagText.length > 0 && (
                    <>
                      {' '}
                      <Tag size="lg" className="option__tag option__tag__plum">
                        {options[selectedIndex].tagText}
                      </Tag>
                    </>
                  )}
                </>
              ) : (
                'Select...'
              )}
            </div>
            <i
              className={cx('zolaicon', {
                'zolaicon-chevron-up': open,
                'zolaicon-chevron-down': !open,
                'icon-match-btn-v2': matchButtonV2,
              })}
            />
          </div>
        )}
        <div
          ref={this.directOptionsContainerRef}
          className={cx('dropdown-options', {
            expanded: open,
            'dropdown-options--top': showOptionsOnTop,
            'dropdown-options--bottom': !showOptionsOnTop,
            'dropdown-radius-match-btn-v2': matchButtonV2,
          })}
        >
          {options.map((option, index) =>
            _includes(disabledOptions, option.value)
              ? this.renderDisabledDropdownItem(option)
              : this.renderDropdownItem(option, index, selectedIndex)
          )}
        </div>
      </div>
    );
  }
}

DropdownSelect.propTypes = {
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  disabledOptions: PropTypes.arrayOf(PropTypes.number),
  onSelect: PropTypes.func,
  alwaysOpen: PropTypes.bool,
  hideLabel: PropTypes.bool,
  isDisabled: PropTypes.bool,
  matchButtonV2: PropTypes.bool,
  hasConfirmSelect: PropTypes.bool,
};

export default DropdownSelect;
